import { useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, Maximize2, TrendingUp } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import {
  fetchStats,
  fetchAnalytics,
  registrationsInconsistent,
  type AdminStats,
  type AdminAnalytics,
} from '@/lib/adapters'
import { ErrorState, Skeleton } from '@/components/States'
import { BarChart, type BarDatum } from '@/components/BarChart'
import { cn } from '@/lib/cn'

/**
 * A2.01 — Umumiy dashboard.
 *
 * Ikki JONLI manba: `/admin/stats` (5 raqam) + `/admin/analytics` (§219 —
 * DAU/MAU/7-kun qatori/ro'yxatdan o'tish/funnel). TZ A2 30+ widget so'raydi;
 * ular ClickHouse (agregatsiya) va Reverb (real-time) ustiga quriladi,
 * ikkalasi ham yo'q. **Bo'sh «tez kunda» kartochkalar chizilmaydi.**
 *
 * ⚠️ `registrations` server tomonda `funnel.total_users` bilan zid
 * (`adapters.registrationsInconsistent` izohiga qara). Raqam yashirilmaydi
 * ham, jimgina ko'rsatilmaydi ham — ogohlantirish bilan beriladi.
 */

function KPICard({
  label,
  value,
  hint,
  loading,
  accent,
}: {
  label: string
  value: number | string | undefined
  hint?: string
  loading: boolean
  accent?: 'warning'
}) {
  return (
    <div
      className={cn(
        'rounded-lg border bg-surface-2 p-3',
        accent === 'warning' ? 'border-warning' : 'border-border-default',
      )}
    >
      {loading ? (
        <Skeleton className="h-8 w-24" />
      ) : (
        <p className="font-mono text-2xl font-semibold text-primary tabular">
          {typeof value === 'number' ? value.toLocaleString() : (value ?? '—')}
        </p>
      )}
      <p className="mt-1 text-sm text-secondary">{label}</p>
      {hint && <p className="mt-0.5 text-xs text-tertiary">{hint}</p>}
    </div>
  )
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-border-default bg-surface-2 p-3">
      <p className="mb-2 text-sm font-medium text-secondary">{title}</p>
      {children}
    </div>
  )
}

/** `2026-07-19` → `19.07` (o'q yorlig'i qisqa bo'lishi kerak). */
function shortDay(iso: string): string {
  const [, m, d] = iso.split('-')
  return m && d ? `${d}.${m}` : iso
}

export function DashboardScreen() {
  const t = useT()
  const [stats, setStats] = useState<AdminStats | null>(null)
  const [analytics, setAnalytics] = useState<AdminAnalytics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)

    Promise.all([fetchStats(ac.signal), fetchAnalytics(ac.signal)])
      .then(([s, a]) => {
        if (!alive) return
        setStats(s)
        setAnalytics(a)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })

    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])

  const bars: BarDatum[] =
    analytics?.messages_per_day.map((d) => ({
      label: shortDay(d.day),
      value: d.messages,
      title: d.day,
    })) ?? []

  const inconsistent = registrationsInconsistent(analytics)
  const rate = analytics ? `${(analytics.funnel.activation_rate * 100).toFixed(1)}%` : undefined

  return (
    <>
      <PageHeader
        title={t.nav.dashboard}
        screenId="A2.01"
        verified
        actions={
          <div className="flex items-center gap-1.5">
            <Link
              to="/growth"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <TrendingUp size={13} />
              {t.dash.growthTitle}
            </Link>
            <Link
              to="/tv"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <Maximize2 size={13} />
              {t.dash.tvTitle}
            </Link>
          </div>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {error ? (
          <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
        ) : (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
              <KPICard
                label={t.dash.users}
                value={stats?.users}
                hint={t.dash.usersHint}
                loading={loading}
              />
              <KPICard label={t.dash.dau} value={analytics?.dau ?? stats?.dau} loading={loading} />
              <KPICard label={t.dash.mau} value={analytics?.mau} loading={loading} />
              <KPICard label={t.dash.messagesToday} value={stats?.messages_today} loading={loading} />
              <KPICard label={t.dash.dialogs} value={stats?.dialogs} loading={loading} />
              <KPICard label={t.dash.mediaFiles} value={stats?.media_files} loading={loading} />
            </div>

            {/* Zid raqam — banner sahifa darajasida, karta yonida emas
                (skill §13: banner = sahifa darajasidagi ogohlantirish). */}
            {inconsistent && analytics && (
              <div className="flex items-start gap-2 rounded-md border border-warning bg-warning-subtle p-2.5">
                <AlertTriangle size={14} className="mt-0.5 shrink-0 text-warning" />
                <div className="text-sm">
                  <p className="font-medium text-warning">{t.dash.inconsistentTitle}</p>
                  <p className="mt-0.5 text-secondary">
                    {t.dash.inconsistentDesc(
                      analytics.registrations.d7,
                      analytics.funnel.total_users,
                    )}
                  </p>
                </div>
              </div>
            )}

            <div className="grid gap-3 lg:grid-cols-3">
              <div className="lg:col-span-2">
                <Panel title={t.dash.messagesPerDay}>
                  {loading ? (
                    // Skeleton yakuniy grafik bilan BIR XIL balandlikda —
                    // layout shift bo'lmaydi (skill §9).
                    <Skeleton className="h-[140px] w-full" />
                  ) : (
                    <BarChart
                      data={bars}
                      emptyLabel={t.dash.noData}
                      tableLabel={t.dash.messagesPerDay}
                    />
                  )}
                </Panel>
              </div>

              <div className="space-y-3">
                <KPICard
                  label={t.dash.activation}
                  value={rate}
                  hint={
                    analytics
                      ? t.dash.activationHint(
                          analytics.funnel.sent_first_message,
                          analytics.funnel.total_users,
                        )
                      : undefined
                  }
                  loading={loading}
                />
                <Panel title={t.dash.registrations}>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { l: t.dash.regToday, v: analytics?.registrations.today },
                      { l: t.dash.regD7, v: analytics?.registrations.d7 },
                      { l: t.dash.regD30, v: analytics?.registrations.d30 },
                    ].map((x) => (
                      <div key={x.l}>
                        {loading ? (
                          <Skeleton className="h-6 w-12" />
                        ) : (
                          <p
                            className={cn(
                              'font-mono text-lg font-semibold tabular',
                              inconsistent ? 'text-warning' : 'text-primary',
                            )}
                          >
                            {x.v?.toLocaleString() ?? '—'}
                          </p>
                        )}
                        <p className="text-xs text-tertiary">{x.l}</p>
                      </div>
                    ))}
                  </div>
                </Panel>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
